/**
 * @file useConsentDraftActions.ts
 * @description Действия экрана-списка с черновиком браузера (phase19.16,
 * Р3г; ревью В2, В3): «Новая редакция» и «Взять за основу» при
 * существующем черновике спрашивают «Продолжить черновик» / «Начать новую —
 * черновик заменится»; «✕» у черновика — подтверждение «Удалить черновик?».
 * Вопросы — окном кабинета (`useConfirm` + `ConfirmDialog`), не `window.confirm`;
 * закрытие окна выбора по Esc ничего не делает (`confirmChoice` → null).
 */

import type { ConsentTextKind } from "@chaiberi/shared";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router";

import { useConfirm, type ConfirmApi } from "../../components/ui/use-confirm";
import { clearConsentDraft, loadConsentDraft } from "../../lib/platform/consent-texts-draft";
import { draftLine } from "../../lib/platform/consent-texts-format";

const NEW_PATH = "/platform/consent-texts/new";

export interface ConsentDraftActions {
  confirmApi: ConfirmApi;
  /** Новая редакция вида; `base` — «Взять за основу» ред. N. */
  startNew: (kind: ConsentTextKind, base?: number) => Promise<void>;
  dropDraft: (kind: ConsentTextKind) => Promise<void>;
  /** Меняется после удаления черновика — список перечитывает хранилище. */
  draftTick: number;
}

export function useConsentDraftActions(): ConsentDraftActions {
  const navigate = useNavigate();
  const confirmApi = useConfirm();
  const { confirm, confirmChoice } = confirmApi;
  const [draftTick, setDraftTick] = useState(0);

  const startNew = useCallback(
    async (kind: ConsentTextKind, base?: number) => {
      const fresh = `${NEW_PATH}?kind=${kind}${base ? `&base=${base}` : ""}`;
      const draft = loadConsentDraft(kind);
      if (!draft) {
        navigate(fresh);
        return;
      }
      const startOver = await confirmChoice({
        title: "Есть несохранённый черновик",
        description: `${draftLine(draft.authorName, draft.savedAt)}. Если начать новую редакцию, черновик заменится при её сохранении.`,
        action: "Начать новую — черновик заменится",
        cancel: "Продолжить черновик",
      });
      // Закрыли окно без выбора (Esc) — остаёмся на списке.
      if (startOver === null) return;
      navigate(startOver ? fresh : `${NEW_PATH}?kind=${kind}&draft=1`);
    },
    [confirmChoice, navigate],
  );

  const dropDraft = useCallback(
    async (kind: ConsentTextKind) => {
      const ok = await confirm({ title: "Удалить черновик?", description: "Правки из него пропадут.", action: "Удалить", danger: true });
      if (!ok) return;
      clearConsentDraft(kind);
      setDraftTick((n) => n + 1);
    },
    [confirm],
  );

  return { confirmApi, startNew, dropDraft, draftTick };
}
